import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Shield, CheckCircle2, AlertCircle, IndianRupee, Users, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const STATUS_PILL = {
    active: "border-emerald-200 bg-emerald-50 text-emerald-700",
    expired: "border-rose-200 bg-rose-50 text-rose-700",
    free:    "border-border bg-secondary text-muted-foreground",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(new Date(iso), "d MMM yyyy"); } catch { return iso; }
};

export default function AdminPaymentsPage() {
    const [status, setStatus] = useState(null);
    const [overview, setOverview] = useState(null);
    const [subscribers, setSubscribers] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    useDocumentTitle("Admin · Payments");

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get("/admin/payments/status"),
            api.get("/admin/overview"),
        ]).then(([s, o]) => {
            if (cancelled) return;
            setStatus(s.data);
            setOverview(o.data);
        });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setSubscribers(null);
        const params = { page_size: 50 };
        if (search.trim()) params.search = search.trim();
        if (statusFilter !== "all") params.status_filter = statusFilter;
        api.get("/admin/payments/subscribers", { params })
            .then((r) => { if (!cancelled) setSubscribers(r.data); });
        return () => { cancelled = true; };
    }, [search, statusFilter]);

    const conversion = overview?.total_users
        ? Math.round((overview.subscribers_active / overview.total_users) * 1000) / 10
        : 0;

    return (
        <Layout>
            <div className="space-y-8 animate-fade-in-up" data-testid="admin-payments-page">
                <header className="space-y-1.5">
                    <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                        <Shield className="h-3.5 w-3.5" />
                        Admin
                    </p>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Payments</h1>
                    <p className="text-sm text-muted-foreground">
                        Razorpay status, subscribers, and revenue.
                    </p>
                </header>

                {/* Configuration banner */}
                {status && !status.configured && (
                    <Card className="border-destructive/50 bg-destructive/5" data-testid="admin-payments-not-configured-banner">
                        <CardContent className="p-4 flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
                            <div className="text-sm space-y-1">
                                <p className="font-medium text-destructive">Razorpay keys not configured</p>
                                <p className="text-muted-foreground">
                                    Ask your developer to set <code className="rounded bg-secondary px-1 py-0.5 text-xs">RAZORPAY_KEY_ID</code>{" "}
                                    and <code className="rounded bg-secondary px-1 py-0.5 text-xs">RAZORPAY_KEY_SECRET</code> in the
                                    backend env. The paywall still triggers — but no one can subscribe yet.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Status panel */}
                <Card className="border-border/80 shadow-sm" data-testid="admin-payments-status-card">
                    <CardContent className="p-5 space-y-3">
                        <h2 className="text-base font-semibold text-foreground">Configuration</h2>
                        {!status ? (
                            <Skeleton className="h-6 w-72" />
                        ) : (
                            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                                <li className="space-y-0.5">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p>
                                    <p className="font-medium flex items-center gap-1.5" data-testid="admin-payments-configured">
                                        {status.configured ? (
                                            <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Configured</>
                                        ) : (
                                            <><AlertCircle className="h-4 w-4 text-destructive" /> Not configured</>
                                        )}
                                    </p>
                                </li>
                                <li className="space-y-0.5">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Key prefix</p>
                                    <p className="font-mono text-xs" data-testid="admin-payments-key-prefix">
                                        {status.key_id_prefix ? `${status.key_id_prefix}_…` : "—"}
                                    </p>
                                </li>
                                <li className="space-y-0.5">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Price</p>
                                    <p className="font-medium tabular-nums">₹{status.price_inr.toLocaleString()} / year</p>
                                </li>
                                <li className="space-y-0.5">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Free quota</p>
                                    <p className="font-medium tabular-nums">{status.free_quota} sessions</p>
                                </li>
                            </ul>
                        )}
                    </CardContent>
                </Card>

                {/* Revenue & conversion stats */}
                <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-payments-stats">
                    <Card className="border-border/80 shadow-sm">
                        <CardContent className="p-5 space-y-1.5">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                <Users className="h-3.5 w-3.5 text-primary" />
                                Active subscribers
                            </div>
                            <p className="text-3xl font-semibold tabular-nums text-foreground" data-testid="admin-payments-active-count">
                                {overview?.subscribers_active ?? "—"}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-border/80 shadow-sm">
                        <CardContent className="p-5 space-y-1.5">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                <AlertCircle className="h-3.5 w-3.5 text-primary" />
                                Expired
                            </div>
                            <p className="text-3xl font-semibold tabular-nums text-foreground" data-testid="admin-payments-expired-count">
                                {overview?.subscribers_expired ?? "—"}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-border/80 shadow-sm">
                        <CardContent className="p-5 space-y-1.5">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                <IndianRupee className="h-3.5 w-3.5 text-primary" />
                                Total revenue
                            </div>
                            <p className="text-3xl font-semibold tabular-nums text-foreground" data-testid="admin-payments-revenue">
                                ₹{(overview?.total_revenue_inr ?? 0).toLocaleString()}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-border/80 shadow-sm">
                        <CardContent className="p-5 space-y-1.5">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Conversion
                            </div>
                            <p className="text-3xl font-semibold tabular-nums text-foreground" data-testid="admin-payments-conversion">
                                {conversion}%
                            </p>
                            <p className="text-xs text-muted-foreground">active / total users</p>
                        </CardContent>
                    </Card>
                </section>

                {/* Subscribers table */}
                <Card className="border-border/80 shadow-sm" data-testid="admin-payments-subscribers-card">
                    <CardContent className="p-0">
                        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center border-b border-border/70">
                            <h2 className="text-base font-semibold text-foreground mr-auto">Subscribers</h2>
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search email or name"
                                className="sm:w-64"
                                data-testid="admin-payments-search"
                            />
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="sm:w-44" data-testid="admin-payments-status-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All statuses</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                    <SelectItem value="free">Free</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {!subscribers ? (
                            <div className="space-y-2 p-4">
                                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full rounded" />)}
                            </div>
                        ) : subscribers.items.length === 0 ? (
                            <p className="p-6 text-center text-sm text-muted-foreground">No subscribers match these filters.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="admin-payments-table">
                                    <thead className="bg-secondary/50 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        <tr>
                                            <th className="px-4 py-3 text-left">User</th>
                                            <th className="px-4 py-3 text-left">Status</th>
                                            <th className="px-4 py-3 text-left hidden sm:table-cell">Started</th>
                                            <th className="px-4 py-3 text-left hidden md:table-cell">Expires</th>
                                            <th className="px-4 py-3 text-right hidden lg:table-cell">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border/70">
                                        {subscribers.items.map((row) => (
                                            <tr key={row.id} className="hover:bg-secondary/40" data-testid={`admin-payments-row-${row.id}`}>
                                                <td className="px-4 py-3">
                                                    <Link to={`/admin/users/${row.id}`} className="block group">
                                                        <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                                                            {row.full_name || row.email}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">{row.email}</p>
                                                    </Link>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={cn("rounded-full border px-2 py-0.5 text-xs font-medium", STATUS_PILL[row.subscription_status])}>
                                                        {row.subscription_status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{fmtDate(row.started_at)}</td>
                                                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                                                    {fmtDate(row.expires_at)}
                                                    {row.days_remaining != null && row.days_remaining > 0 && (
                                                        <span className="ml-1 text-xs">· {row.days_remaining}d left</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums hidden lg:table-cell">
                                                    {row.amount ? `₹${(row.amount / 100).toLocaleString()}` : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <Mail className="h-3 w-3" />
                    Support: connect@thehappylab.in
                </p>
            </div>
        </Layout>
    );
}
